import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  Input,
  Button,
  Space,
  Progress,
  Typography,
  Alert,
  message,
  Select,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  generateSoraVideo,
  getSoraVideoStatus,
  uploadLocalImage,
} from '@/services/xiaohongshu';

const { TextArea } = Input;
const { Text } = Typography;

interface VideoEditorProps {
  visible: boolean;
  onCancel: () => void;
  onFinish?: (videoUrl: string) => void;
  defaultPrompt?: string;
  referenceImage?: string;
  aspectRatio?: '9:16' | '16:9';
  duration?: number;
}

type GenerationState = 'idle' | 'generating' | 'succeeded' | 'failed';

interface SoraStatusResponse {
  status?: string;
  progress?: number;
  videoUrl?: string;
  reason?: string;
  [key: string]: any;
}

const POLLING_INTERVAL = 5000;

const normalizeDuration = (value?: number): number | undefined =>
  value === 10 || value === 15 ? value : undefined;

const ALLOWED_REFERENCE_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

const VideoEditor: React.FC<VideoEditorProps> = ({
  visible,
  onCancel,
  onFinish,
  defaultPrompt = '',
  referenceImage,
  aspectRatio,
  duration,
}) => {
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<
    '9:16' | '16:9' | undefined
  >(aspectRatio);
  const [selectedDuration, setSelectedDuration] = useState<number | undefined>(
    normalizeDuration(duration),
  );
  const [referenceImageUrl, setReferenceImageUrl] = useState<
    string | undefined
  >(referenceImage);
  const [jobId, setJobId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [generationState, setGenerationState] =
    useState<GenerationState>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const pollingTimer = useRef<number>();

  const resetState = useCallback(() => {
    setJobId(null);
    setVideoUrl('');
    setGenerationState('idle');
    setProgress(0);
    setStatusMessage('');
    setErrorMessage('');
    setSelectedAspectRatio(aspectRatio);
    setSelectedDuration(normalizeDuration(duration));
    setReferenceImageUrl(referenceImage);
    setIsUploading(false);
  }, [aspectRatio, duration, referenceImage]);

  useEffect(() => {
    if (visible) {
      setPrompt(defaultPrompt);
      setSelectedAspectRatio(aspectRatio);
      setSelectedDuration(normalizeDuration(duration));
      setReferenceImageUrl(referenceImage);
    } else {
      if (pollingTimer.current) {
        window.clearInterval(pollingTimer.current);
        pollingTimer.current = undefined;
      }
      resetState();
    }
  }, [
    visible,
    defaultPrompt,
    aspectRatio,
    duration,
    referenceImage,
    resetState,
  ]);

  useEffect(() => {
    return () => {
      if (pollingTimer.current) {
        window.clearInterval(pollingTimer.current);
      }
    };
  }, []);

  const updateProgress = useCallback((status: SoraStatusResponse) => {
    const rawProgress =
      typeof status.progress === 'number' ? status.progress : 0;
    setProgress(Math.min(100, Math.max(0, rawProgress)));

    if (status.status) {
      setStatusMessage(status.status);
    }

    if (status.videoUrl) {
      setVideoUrl(status.videoUrl);
    }

    if (status.reason) {
      setErrorMessage(status.reason);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingTimer.current) {
      window.clearInterval(pollingTimer.current);
      pollingTimer.current = undefined;
    }
  }, []);

  const handlePolling = useCallback(
    async (currentJobId: string) => {
      try {
        const response: any = await getSoraVideoStatus({ jobId: currentJobId });
        const status: SoraStatusResponse = response?.resp?.data || {};

        updateProgress(status);

        const normalizedStatus = (status.status || '').toLowerCase();

        if (
          normalizedStatus === 'succeeded' ||
          normalizedStatus === 'success'
        ) {
          stopPolling();
          setGenerationState('succeeded');
          if (status.videoUrl) {
            setVideoUrl(status.videoUrl);
            message.success('视频生成完成');
          }
        } else if (
          normalizedStatus === 'failed' ||
          normalizedStatus === 'error'
        ) {
          stopPolling();
          setGenerationState('failed');
          const reason = status.reason || '视频生成失败，请稍后重试';
          setErrorMessage(reason);
          message.error(reason);
        } else {
          setGenerationState('generating');
        }
      } catch (err) {
        console.error('[VideoEditor] 获取 Sora 视频进度失败:', err);
        stopPolling();
        setGenerationState('failed');
        const reason = '获取生成进度失败，请稍后重试';
        setErrorMessage(reason);
        message.error(reason);
      }
    },
    [stopPolling, updateProgress],
  );

  const startPolling = useCallback(
    (currentJobId: string) => {
      if (pollingTimer.current) {
        window.clearInterval(pollingTimer.current);
      }

      pollingTimer.current = window.setInterval(() => {
        handlePolling(currentJobId);
      }, POLLING_INTERVAL);

      // 立即触发一次，提升首次反馈速度
      handlePolling(currentJobId);
    },
    [handlePolling],
  );

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      message.warning('请输入 Prompt 描述');
      return;
    }

    try {
      setGenerationState('generating');
      setProgress(0);
      setStatusMessage('正在提交视频生成任务');
      setErrorMessage('');
      setVideoUrl('');

      const payload = {
        prompt: prompt.trim(),
        ...(referenceImageUrl ? { referenceImage: referenceImageUrl } : {}),
        ...(selectedAspectRatio ? { aspectRatio: selectedAspectRatio } : {}),
        ...(typeof selectedDuration === 'number'
          ? { duration: selectedDuration }
          : {}),
      } as Parameters<typeof generateSoraVideo>[0];

      const response: any = await generateSoraVideo(payload);
      const nextJobId: string | undefined =
        response?.resp?.data?.jobId ?? response?.resp?.jobId;

      if (!nextJobId) {
        throw new Error('未获取到有效的任务 ID');
      }

      setJobId(nextJobId);
      startPolling(nextJobId);
      message.success('视频生成任务已创建');
    } catch (err) {
      console.error('[VideoEditor] 提交 Sora 视频生成失败:', err);
      setGenerationState('failed');
      const reason =
        err instanceof Error ? err.message : '视频生成失败，请稍后重试';
      setErrorMessage(reason);
      message.error(reason);
    }
  }, [
    prompt,
    referenceImageUrl,
    selectedAspectRatio,
    selectedDuration,
    startPolling,
  ]);

  const handleReferenceImageUpload: UploadProps['beforeUpload'] = async (
    file,
  ) => {
    if (!file) {
      return Upload.LIST_IGNORE;
    }

    const mimeType = file.type || '';
    const fileExt = file.name?.split('.').pop()?.toLowerCase() || '';
    const isAllowedType =
      ALLOWED_REFERENCE_IMAGE_TYPES.has(mimeType) ||
      ['jpg', 'jpeg', 'png', 'webp'].includes(fileExt);

    if (!isAllowedType) {
      message.warning('仅支持上传 JPEG、PNG 或 WEBP 格式图片');
      return Upload.LIST_IGNORE;
    }

    if (generationState === 'generating') {
      message.warning('生成过程中无法上传图片');
      return Upload.LIST_IGNORE;
    }

    try {
      setIsUploading(true);
      const response: any = await uploadLocalImage({ file: file as File });
      const uploadedUrl = response?.resp?.url ?? response?.resp?.data?.url;
      if (response?.resp?.code === 0 && uploadedUrl) {
        setReferenceImageUrl(uploadedUrl);
        message.success('参考图片上传成功');
      } else {
        message.error('参考图片上传失败，请稍后重试');
      }
    } catch (error) {
      console.error('[VideoEditor] 上传参考图片失败:', error);
      message.error('参考图片上传失败，请稍后重试');
    } finally {
      setIsUploading(false);
    }

    return Upload.LIST_IGNORE;
  };

  const handleCancel = useCallback(() => {
    stopPolling();
    resetState();
    onCancel();
  }, [onCancel, resetState, stopPolling]);

  const statusLabel = useMemo(() => {
    if (generationState === 'idle') return '等待生成';
    if (generationState === 'generating') return statusMessage || '生成中...';
    if (generationState === 'succeeded') return '生成完成';
    if (generationState === 'failed') return errorMessage || '生成失败';
    return '';
  }, [generationState, statusMessage, errorMessage]);

  const footerButtons = useMemo(() => {
    if (generationState === 'succeeded' && videoUrl) {
      return [
        <Button key="close" onClick={handleCancel}>
          关闭
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={() => {
            onFinish?.(videoUrl);
            handleCancel();
          }}
        >
          使用该视频
        </Button>,
      ];
    }

    return [
      <Button
        key="cancel"
        onClick={handleCancel}
        disabled={generationState === 'generating'}
      >
        取消
      </Button>,
      <Button
        key="generate"
        type="primary"
        onClick={handleGenerate}
        loading={generationState === 'generating' && !!jobId}
      >
        {generationState === 'generating' ? '生成中' : '开始生成'}
      </Button>,
    ];
  }, [
    generationState,
    videoUrl,
    handleCancel,
    handleGenerate,
    jobId,
    onFinish,
  ]);

  return (
    <Modal
      title="Sora 视频生成"
      open={visible}
      onCancel={handleCancel}
      footer={footerButtons}
      width={720}
      maskClosable={generationState !== 'generating'}
      closable={generationState !== 'generating'}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            flex: '1 1 360px',
            minWidth: 320,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Prompt 描述
            </Text>
            <TextArea
              placeholder="请输入视频生成的描述"
              autoSize={{ minRows: 4, maxRows: 8 }}
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              disabled={generationState === 'generating'}
            />
          </div>

          <Space size="large" align="start" wrap>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                画幅比例
              </Text>
              <Select
                placeholder="选择画幅比例"
                style={{ width: 160 }}
                value={selectedAspectRatio}
                onChange={(value) =>
                  setSelectedAspectRatio(
                    (value as '9:16' | '16:9' | undefined) ?? undefined,
                  )
                }
                options={[
                  { label: '16:9 横屏', value: '16:9' },
                  { label: '9:16 竖屏', value: '9:16' },
                ]}
                allowClear
                disabled={generationState === 'generating'}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                视频时长（秒）
              </Text>
              <Select
                placeholder="选择时长"
                style={{ width: 160 }}
                value={selectedDuration}
                onChange={(value) =>
                  setSelectedDuration(
                    normalizeDuration(value as number | undefined),
                  )
                }
                options={[
                  { label: '10 秒', value: 10 },
                  { label: '15 秒', value: 15 },
                ]}
                allowClear
                disabled={generationState === 'generating'}
              />
            </div>
          </Space>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              参考图片
            </Text>
            <Space align="start" size="large" wrap>
              <Upload
                accept="image/jpeg,image/png,image/webp"
                showUploadList={false}
                beforeUpload={handleReferenceImageUpload}
                disabled={generationState === 'generating' || isUploading}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={isUploading}
                  disabled={generationState === 'generating' || isUploading}
                >
                  上传参考图
                </Button>
              </Upload>
              {referenceImageUrl && (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 8,
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <img
                    src={referenceImageUrl}
                    alt="参考图片"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Button
                    type="link"
                    danger
                    size="small"
                    style={{ position: 'absolute', bottom: 4, right: 4 }}
                    onClick={() => setReferenceImageUrl(undefined)}
                  >
                    移除
                  </Button>
                </div>
              )}
            </Space>
          </div>

          {generationState !== 'idle' && (
            <div>
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="small"
              >
                <Text
                  type={generationState === 'failed' ? 'danger' : undefined}
                >
                  {statusLabel}
                </Text>
                <Progress
                  percent={Math.round(progress)}
                  status={
                    generationState === 'failed' ? 'exception' : undefined
                  }
                />
              </Space>
            </div>
          )}

          {errorMessage && generationState === 'failed' && (
            <Alert type="error" showIcon message={errorMessage} />
          )}
        </div>

        <div
          style={{
            flex: '1 1 280px',
            minWidth: 260,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <Text strong>视频预览</Text>
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              height: 320,
              maxHeight: 360,
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              backgroundColor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {videoUrl ? (
              <video
                src={videoUrl}
                controls
                style={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Text style={{ color: '#fff', opacity: 0.65 }}>暂无视频预览</Text>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default VideoEditor;
